import {
  useExportEquipment,
  useListEquipment,
} from "#hooks/adapters/useEquipment";
import { useGetOneFacility } from "#hooks/adapters/useFacilities";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#redux/reducers/tableStateReducer";
import { useParams } from "#routers/hooks";
import { linkToEquipmentDetail } from "#routers/links";
import type { FacilityDetailPageParams } from "#routes/organization/facilities/[facilityId]";
import { ImportDataActionStatusWithQueue } from "#src/batteries-included-components/Banners/ImportDataActionStatus";
import { CreateAssetDropdownMenu } from "#src/batteries-included-components/Buttons/CreateAssetDropdownMenu";
import { ImportDataAction } from "#src/batteries-included-components/Buttons/ImportDataAction";
import { getImportDataActionQueueKey } from "#src/batteries-included-components/Buttons/ImportDataAction/ImportDataAction.helpers";
import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import { EquipmentTableFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink/RoutingLink";
import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import {
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaExtendedHeaders,
  useDataTableMetaHeaders,
} from "#src/utils/table";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  Dialog,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  Icon,
  Pill,
  useToast,
  type StorageKeys,
} from "@validereinc/common-components";
import {
  AssetType,
  EquipmentAdapter,
  EquipmentAdapterQueryKeys,
  EquipmentType,
  isResponseErrorType,
  ResourceDefinitions,
  Resources,
  SortDirection,
  type GroupedByEntryType,
  type ResponseErrorType,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  HTTPForbiddenError,
  HTTPNotFoundError,
  HTTPResponseError,
  toFlattenedObject,
  toSnakeCaseString,
  toTitleCaseString,
} from "@validereinc/utilities";
import pLimit from "p-limit";
import React, { useCallback, useMemo, useState } from "react";

export const FacilityEquipmentPanel = ({
  viewConfigStorageKey,
  groupByTablesStateStorageKey,
}: Pick<
  StorageKeys,
  "viewConfigStorageKey" | "groupByTablesStateStorageKey"
>) => {
  const { facilityId } = useParams<FacilityDetailPageParams>();

  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const [tableConfigStorageKey, filterConfigStorageKey] = useStorageKey(
    ["table-configuration", "filter-configuration"],
    ["facilities-equipment"]
  );

  const [tableStateStorageKey] = useStorageKey(
    ["table-state"],
    ["facilities-equipment", facilityId]
  );
  const { localize } = useLocalization();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const importDataActionQueueKey = getImportDataActionQueueKey({
    resource: {
      ...ResourceDefinitions.equipment,
      label: {
        singular: localize(ResourceDefinitions.equipment.label.singular),
        plural: localize("equipment_plural"),
      },
    },
  });
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedRows, setSelectedRows] = useState<
    Record<string, EquipmentType>
  >({});
  const [viewFilters] = useStickyState<any>({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState<any>({}, filterConfigStorageKey);
  const { data: facility } = useGetOneFacility(facilityId, {
    period: getYearMonthFromDateRange(viewFilters.period),
  });
  const { customAttributeHeaders, isLoading: isCustomAttributesLoading } =
    useCustomAttributeFiltersV2({
      assetType: AssetType.EQUIPMENT,
      groupable: true,
    });
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
      pageSize: 25,
    },
    { ...tableFilters, facilityId },
    tableStateStorageKey
  );

  const equipmentApiParams: Parameters<typeof EquipmentAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: {
      ...toFlattenedObject(tableFilters),
      "facility.id": facilityId,
    },
    meta: {
      // @ts-expect-error TODO: Typing on period
      period: getYearMonthFromDateRange(viewFilters.period),
    },
  };
  const { data, isLoading, isFetching } = useListEquipment(equipmentApiParams);
  const items = data?.data ?? [];

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...equipmentApiParams,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? equipmentApiParams)
  );

  const equipmentGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: EquipmentAdapterQueryKeys.getList(d.payload),
      queryFn: () => EquipmentAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const metaHeaders = useDataTableMetaHeaders();
  const metaExtendedHeaders = useDataTableMetaExtendedHeaders(
    tableState.groupBy.length
      ? equipmentGroupedByQueries.flatMap((q) => q.data?.data ?? [])
      : items
  );

  const { mutate: handleExport, isLoading: isExporting } = useExportEquipment(
    equipmentApiParams,
    facility?.name ? toSnakeCaseString(facility.name) : undefined
  );

  const deleteMutation = useMutation({
    mutationFn: (params: Parameters<typeof EquipmentAdapter.deleteOne>[0]) => {
      return EquipmentAdapter.deleteOne(params);
    },
    onSuccess: (_, params) => {
      toast.push({
        intent: "success",
        description: `Successfully deleted ${localize(Resources.EQUIPMENT)}${params.previousData?.name ? ` ${params.previousData?.name}.` : "."}`,
      });
      queryClient.invalidateQueries({ queryKey: [Resources.EQUIPMENT] });
    },
    onError: (error) => {
      if (
        error instanceof HTTPResponseError &&
        error.responseBodyJson &&
        isResponseErrorType(error.responseBodyJson)
      ) {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.EQUIPMENT)} due to a server error: ${error.responseBodyJson.error}`,
        });
      } else {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.EQUIPMENT)}. An unknown error occurred.`,
        });
      }
    },
  });

  const actionRow = [
    <CreateAssetDropdownMenu
      key="add-equipment"
      shouldUseLightVariant
      assetType={AssetType.EQUIPMENT}
    />,
    <PermissionAwareExportButton
      key="export-devices"
      onClick={handleExport}
      isExporting={isExporting}
    />,
    isDataIngestionEnabled ? (
      <ImportDataAction
        key="import-equipment"
        resource={{
          ...ResourceDefinitions.equipment,
          label: {
            singular: localize(ResourceDefinitions.equipment.label.singular),
            plural: localize("equipment_plural"),
          },
        }}
      />
    ) : null,
  ];

  const coreHeaders: Array<HeaderType<EquipmentType>> = useMemo(
    () => [
      {
        key: "name",
        label: "Name",
        isSortable: true,
        renderComponent: ({ item }) => (
          <RoutingLink to={linkToEquipmentDetail(item?.id)}>
            {item.name}
          </RoutingLink>
        ),
      },
      {
        key: "type.name",
        label: "Type",
        isSortable: false,
        isGroupable: true,
        renderComponent: ({ item }) => item?.type?.name ?? "-",
      },
      {
        label: "Status",
        key: "status",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <Pill variant={item.status === "active" ? "success" : "default"}>
            {toTitleCaseString(item.status, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        ),
      },
      {
        label: "Effective Date",
        key: "effective_date",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell value={item.effective_date} />
        ),
      },
    ],
    []
  );
  const headers: Array<HeaderType<EquipmentType>> = useMemo(
    () => [
      ...coreHeaders,
      ...customAttributeHeaders,
      ...metaHeaders,
      ...metaExtendedHeaders,
    ],
    [coreHeaders, customAttributeHeaders, metaHeaders, metaExtendedHeaders]
  );

  const getItemBulkActions = useCallback(
    ({ buttonProps }) => [
      <IsFeatureAvailable
        key="delete"
        featureFlagQuery="core:equipment"
        permissionQuery="equipment:delete"
        fallbackChildren={null}
      >
        <Button
          {...buttonProps}
          variant="error-outline"
          slotLeft={<Icon variant="trash" />}
          onClick={() => setIsDeleteConfirmationOpen(true)}
        >
          Delete
        </Button>
      </IsFeatureAvailable>,
    ],
    []
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    getItemId: (item) => item.id,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: equipmentGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemBulkActions,
    totalSelectableItems,
    selected: selectedRows,
    onSelectionChange: setSelectedRows,
  });

  return (
    <>
      {isDataIngestionEnabled ? (
        <ImportDataActionStatusWithQueue queueKey={importDataActionQueueKey} />
      ) : null}
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          title: localize("equipment_plural"),
          actionRow,
        }}
        filterComponent={
          <EquipmentTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            hasCustomAttributeFilters
            hasFacilityFilters={false}
          />
        }
        dataTableProps={{
          intent: "simplicity-first",
          headers,
          items,
          isLoading: isLoading || isCustomAttributesLoading,
          isBusy: isFetching,
          getItemId: (item) => item.id,
          getItemBulkActions,
          selected: selectedRows,
          onSelectionChange: setSelectedRows,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries,
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
        }}
        {...groupByDataTableProps}
      />
      <IsFeatureAvailable
        featureFlagQuery="core:equipment"
        permissionQuery="equipment:delete"
        fallbackChildren={null}
      >
        <Dialog
          title={`Delete ${localize(`${Resources.EQUIPMENT}_plural`)}?`}
          isOpen={isDeleteConfirmationOpen}
          onClose={() => setIsDeleteConfirmationOpen(false)}
          actionRow={[
            <Button
              key="submit"
              variant="error"
              isLoading={deleteMutation.isLoading}
              onClick={() => {
                const selectedRowIds = Object.keys(selectedRows);
                const limitPromises = pLimit(8);

                setIsDeleteConfirmationOpen(false);
                toast
                  .pushAsync(
                    Promise.all(
                      selectedRowIds.map((id) =>
                        limitPromises(async () =>
                          deleteMutation.mutateAsync({
                            id,
                            previousData: selectedRows[id],
                          })
                        )
                      )
                    ),
                    {
                      intent: "note",
                      description: `Deleting ${selectedRowIds.length} selected ${localize(`${Resources.EQUIPMENT}_plural`)}...`,
                      isBusy: deleteMutation.isLoading,
                    },
                    {
                      isDismissible: false,
                      duration: Infinity,
                    }
                  )
                  .then(({ data, toast: firstToast }) => {
                    toast.push(
                      {
                        intent: "success",
                        description: `Successfully deleted ${data.length} ${localize(`${Resources.EQUIPMENT}_plural`)}`,
                      },
                      {
                        id: firstToast.id,
                        isDismissible: true,
                        duration: 5000,
                      }
                    );
                    setSelectedRows({});
                  })
                  .catch(({ error, toastId }) => {
                    const isNotFoundError = error instanceof HTTPNotFoundError;
                    const isForbiddenError =
                      error instanceof HTTPForbiddenError;
                    const isNodeAPIError =
                      error instanceof HTTPResponseError &&
                      isResponseErrorType(error.responseBodyJson);

                    toast.push(
                      {
                        intent: "error",
                        description: isNodeAPIError
                          ? `Failed to delete selected ${localize(`${Resources.EQUIPMENT}_plural`)}: ${(error.responseBodyJson as unknown as ResponseErrorType | undefined)?.error}`
                          : isNotFoundError || isForbiddenError
                            ? `Failed to delete selected ${localize(`${Resources.EQUIPMENT}_plural`)}: ${error.getExplanation()}`
                            : `Failed to delete ${localize(`${Resources.EQUIPMENT}_plural`)}.`,
                      },
                      {
                        id: toastId,
                        isDismissible: true,
                        duration: 5000,
                      }
                    );
                  })
                  .finally(() => {
                    queryClient.invalidateQueries({
                      queryKey: [Resources.EQUIPMENT],
                    });
                  });
              }}
            >
              Delete
            </Button>,
          ]}
        >
          You&apos;re deleting {Object.keys(selectedRows).length}&nbsp;
          {localize(`${Resources.EQUIPMENT}_plural`)}. This action cannot be
          undone. Are you sure you want to continue?
        </Dialog>
      </IsFeatureAvailable>
    </>
  );
};
